import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import stageIcon from "../../Assets/Icons/trophy.png";
import LoaderBox from "../HomePage/LoaderBox";
import AdminChampionshipBracket from "./AdminChampionshipBracket";

const getProgressionMap = (size) => {
  if (size === 16) {
    return {
      1: { nextSlot: 9, side: "t1" },
      2: { nextSlot: 9, side: "t2" },
      3: { nextSlot: 10, side: "t1" },
      4: { nextSlot: 10, side: "t2" },
      5: { nextSlot: 11, side: "t1" },
      6: { nextSlot: 11, side: "t2" },
      7: { nextSlot: 12, side: "t1" },
      8: { nextSlot: 12, side: "t2" },
      9: { nextSlot: 13, side: "t1" },
      10: { nextSlot: 13, side: "t2" },
      11: { nextSlot: 14, side: "t1" },
      12: { nextSlot: 14, side: "t2" },
      13: { nextSlot: 15, side: "t1" },
      14: { nextSlot: 15, side: "t2" },
    };
  } else if (size === 8) {
    return {
      1: { nextSlot: 5, side: "t1" },
      2: { nextSlot: 5, side: "t2" },
      3: { nextSlot: 6, side: "t1" },
      4: { nextSlot: 6, side: "t2" },
      5: { nextSlot: 7, side: "t1" },
      6: { nextSlot: 7, side: "t2" },
    };
  } else if (size === 4) {
    return {
      1: { nextSlot: 3, side: "t1" },
      2: { nextSlot: 3, side: "t2" },
    };
  }
  return {};
};

// Funkcija za poražene definirana na vrhu
const getLoserProgressionMap = (size) => {
  if (size === 16) {
    return {
      13: { nextSlot: 16, side: "t1" },
      14: { nextSlot: 16, side: "t2" },
    };
  } else if (size === 8) {
    return {
      5: { nextSlot: 8, side: "t1" },
      6: { nextSlot: 8, side: "t2" },
    };
  } else if (size === 4) {
    return {
      1: { nextSlot: 4, side: "t1" },
      2: { nextSlot: 4, side: "t2" },
    };
  }
  return {};
};

const AdminChampionship = ({ selectedTurnirId }) => {
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [championshipSize, setChampionshipSize] = useState(16);

  // Dinamički slotovi ovisno o veličini
  const initialMatchCount = championshipSize / 2;
  const slotData = Array.from({ length: initialMatchCount }, (_, i) => ({
    id: i + 1,
  }));
  const progressionMap = getProgressionMap(championshipSize);
  // Ovdje definiramo loserProgressionMap da bude prije useEffect-a
  const loserProgressionMap = getLoserProgressionMap(championshipSize);

  const thirdPlaceSlot =
    championshipSize === 16
      ? 16
      : championshipSize === 8
        ? 8
        : championshipSize === 4
          ? 4
          : 2;

  const fetchInitialData = useCallback(async () => {
    if (!selectedTurnirId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Dohvat postavke championship_size iz baze
    const { data: turnirData, error: turnirError } = await supabase
      .from("admin_turniri")
      .select("championship_size")
      .eq("id", selectedTurnirId)
      .single();

    if (!turnirError && turnirData) {
      setChampionshipSize(turnirData.championship_size || 16);
    }

    const { data: teamsData, error: teamsError } = await supabase
      .from("turniri_timovi")
      .select("*")
      .eq("turnir_id", selectedTurnirId);

    if (teamsError) console.error("Greška kod dohvaćanja timova:", teamsError);

    const grouped = {};
    (teamsData || []).forEach((t) => {
      const gName = t.group_name || t.grupa || "A";
      if (!grouped[gName]) grouped[gName] = [];
      grouped[gName].push(t);
    });

    const allTeamsWithLabels = [];
    Object.keys(grouped)
      .sort()
      .forEach((gName) => {
        grouped[gName].forEach((team, index) => {
          allTeamsWithLabels.push({
            ...team,
            bracketLabel: `${gName}${index + 1}`,
          });
        });
      });

    const { data: matchData, error: matchError } = await supabase
      .from("turniri_utakmice")
      .select(`*, team_a:team_a_id(id, name), team_b:team_b_id(id, name)`)
      .eq("tournament_id", selectedTurnirId)
      .eq("group_name", "Završnica");

    if (matchError) console.error("Greška kod utakmica:", matchError);

    setFilteredTeams(allTeamsWithLabels);
    setMatches(matchData || []);
    setLoading(false);
  }, [selectedTurnirId]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const updateNextRound = useCallback(
    async (nextSlot, side, winnerId) => {
      // 1. Dohvati SVE utakmice za taj slot da spriječimo duplikate u bazi
      const { data: existingMatches, error: fetchErr } = await supabase
        .from("turniri_utakmice")
        .select("*")
        .eq("tournament_id", selectedTurnirId)
        .eq("bracket_slot_id", nextSlot);

      if (fetchErr) {
        console.error("Greška kod provjere slotova:", fetchErr);
        return;
      }

      const payload =
        side === "t1" ? { team_a_id: winnerId } : { team_b_id: winnerId };

      if (existingMatches && existingMatches.length > 0) {
        // Uzmi prvi pronađeni red, a sve eventualne viškove (duplikate) obriši da ne stvaraju kaos
        const mainMatch = existingMatches[0];

        const currentId =
          side === "t1" ? mainMatch.team_a_id : mainMatch.team_b_id;
        if (currentId === winnerId && existingMatches.length === 1) return; // Već je sve ispravno postavljeno

        // Ažuriraj glavni red
        await supabase
          .from("turniri_utakmice")
          .update(payload)
          .eq("id", mainMatch.id);

        // Ako kojim slučajem već postoje duplikati u bazi, obriši ih
        if (existingMatches.length > 1) {
          const duplicateIds = existingMatches.slice(1).map((m) => m.id);
          await supabase
            .from("turniri_utakmice")
            .delete()
            .in("id", duplicateIds);
        }
      } else {
        // Ako red uopće ne postoji, stvori ga
        await supabase.from("turniri_utakmice").insert([
          {
            ...payload,
            tournament_id: selectedTurnirId,
            bracket_slot_id: nextSlot,
            group_name: "Završnica",
            is_completed: false,
            scorea: 0,
            scoreb: 0,
            match_day: "",
            match_time: "",
            events: [],
            rostera: [],
            rosterb: [],
          },
        ]);
      }
      fetchInitialData();
    },
    [fetchInitialData, selectedTurnirId],
  );

  useEffect(() => {
    const advance = async () => {
      for (const m of matches) {
        if (m.is_completed && m.scorea !== m.scoreb) {
          const winnerId = m.scorea > m.scoreb ? m.team_a_id : m.team_b_id;
          const loserId = m.scorea > m.scoreb ? m.team_b_id : m.team_a_id;

          // 1. Gurni pobjednika dalje (npr. u finale)
          const prog = progressionMap[m.bracket_slot_id];
          if (prog && winnerId) {
            await updateNextRound(prog.nextSlot, prog.side, winnerId);
          }

          // 2. Gurni poraženog u utakmicu za 3. mjesto
          const loserProg = loserProgressionMap[m.bracket_slot_id];
          if (loserProg && loserId) {
            await updateNextRound(loserProg.nextSlot, loserProg.side, loserId);
          }
        }
      }
    };
    if (matches.length > 0) advance();
  }, [matches, updateNextRound, progressionMap, loserProgressionMap]);

  const handleTeamSelect = async (slotId, side, teamId) => {
    const validId = teamId === "" ? null : teamId;
    const existing = matches.find((m) => m.bracket_slot_id === slotId);
    const payload =
      side === "t1" ? { team_a_id: validId } : { team_b_id: validId };

    if (existing) {
      await supabase
        .from("turniri_utakmice")
        .update(payload)
        .eq("id", existing.id);
    } else {
      await supabase.from("turniri_utakmice").insert([
        {
          ...payload,
          tournament_id: selectedTurnirId,
          bracket_slot_id: slotId,
          group_name: "Završnica",
          match_time: "",
          match_day: "",
          is_completed: false,
          scorea: 0,
          scoreb: 0,
          events: [],
          rostera: [],
          rosterb: [],
        },
      ]);
    }
    fetchInitialData();
  };

  const handleSizeChange = async (newSize) => {
    const sizeNum = Number(newSize);
    if (
      !window.confirm(
        `Jesi li siguran da želiš postaviti završnicu na ${sizeNum} ekipa?`,
      )
    )
      return;

    setChampionshipSize(sizeNum);
    const { error } = await supabase
      .from("admin_turniri")
      .update({ championship_size: sizeNum })
      .eq("id", selectedTurnirId);

    if (error) {
      alert("Greška pri spremanju: " + error.message);
    } else {
      fetchInitialData();
    }
  };

  const getM = (id) => matches.find((m) => m.bracket_slot_id === id) || {};
  const isTeamDisabled = (tid) =>
    matches.some(
      (m) =>
        (m.team_a_id === tid || m.team_b_id === tid) &&
        m.bracket_slot_id <= initialMatchCount,
    );

  if (loading) return <LoaderBox text="Učitavanje..." />;

  return (
    <AdminChampionshipBracket
      slotData={slotData}
      championshipSize={championshipSize}
      handleSizeChange={handleSizeChange}
      thirdPlaceSlot={thirdPlaceSlot}
      filteredTeams={filteredTeams}
      getM={getM}
      handleTeamSelect={handleTeamSelect}
      isTeamDisabled={isTeamDisabled}
      stageIcon={stageIcon}
    />
  );
};

export default AdminChampionship;
